const STORAGE_KEY = "nft:module-reload-at";
const RETRY_COOLDOWN_MS = 5 * 60_000;

export function isModuleLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script|Unable to preload CSS/i.test(message);
}

export function reloadCurrentPage() {
  window.location.reload();
}

type RecoveryEnvironment = {
  getStorage: () => Pick<Storage, "getItem" | "setItem">;
  isOnline: () => boolean;
  now: () => number;
  reload: () => void;
};

const browserEnvironment: RecoveryEnvironment = {
  getStorage: () => window.sessionStorage,
  isOnline: () => navigator.onLine,
  now: () => Date.now(),
  reload: reloadCurrentPage
};

/** A stale deployment may need a fresh document, but repeated failures must not cause a reload loop. */
export function reloadAfterModuleError(error: unknown, environment: RecoveryEnvironment = browserEnvironment): boolean {
  if (!isModuleLoadError(error) || !environment.isOnline()) return false;
  try {
    const storage = environment.getStorage();
    const lastReload = Number(storage.getItem(STORAGE_KEY));
    const now = environment.now();
    if (lastReload > 0 && now - lastReload < RETRY_COOLDOWN_MS) return false;
    // Store before reloading; the guard survives the full document reload.
    storage.setItem(STORAGE_KEY, String(now));
    environment.reload();
    return true;
  } catch {
    // Without working storage there is no reliable loop guard: leave recovery to the user.
    return false;
  }
}

export function installModuleRecovery() {
  const handleError = (event: Event) => {
    // Leave Vite's exception intact so the route error screen remains available if reload fails.
    reloadAfterModuleError((event as Event & { payload?: unknown }).payload);
  };
  window.addEventListener("vite:preloadError", handleError);
  return () => window.removeEventListener("vite:preloadError", handleError);
}
